import type { Maybe, Discount, ID, ProductListItem } from '@/types'

import { findByPropValue, getFileUrl } from '@/helpers'

import { CUSTOMER_PRODUCT_PATH, DEFAULT_IMAGE_SRC } from '@/constants'

export function getImageUrl(
  index: number,
  item?: any,
  defaultValue: Maybe<string> = DEFAULT_IMAGE_SRC,
  template = 'm_upload'
) {
  const path = item?.images?.[index]?.path
  if (!path) return defaultValue

  const url = getFileUrl(`${template}/${path}`)
  if (!url) return defaultValue

  return url
}

export function getImagePath(item: any, hasImage?: boolean) {
  if (!hasImage) return null

  return getImageUrl(0, item, null)
}

export function getImageSrcSet(
  srcSetArray: string[][],
  imagePath?: Maybe<string>,
  defaultValue: string = DEFAULT_IMAGE_SRC
): { src: string; srcSet?: string[]; src2x?: string } {
  const src = imagePath
    ? getFileUrl(`${srcSetArray[0][0]}/${imagePath}`)
    : defaultValue

  if (!imagePath) return { src }

  return {
    srcSet: prepareImagePathTemplates(srcSetArray, imagePath),
    src,
    src2x: getFileUrl(`${srcSetArray[0][1]}/${imagePath}`),
  }
}

export function prepareImagePathTemplates(
  srcSetArray: string[][],
  imagePath: string
) {
  return srcSetArray.map((templates) =>
    templates
      .map(
        (template, i) =>
          getFileUrl(`${template}/${imagePath}`) + (i > 0 ? ' 2x' : '')
      )
      .join(', ')
  )
}

export function getImageAlt(index: number, item?: any, key = 'name') {
  return item?.images?.[index]?.alt ?? item?.[key] ?? ''
}

export function prepareProductUrl(formUrl?: Maybe<string>) {
  const host = import.meta.env.VITE_HOST ?? ''
  const path = `${host}${CUSTOMER_PRODUCT_PATH}`

  if (!formUrl) return `${path}`

  return `${path}${formUrl}`
}

export function formatPrice(
  value?: number | string,
  locale = 'ru-RU',
  options = {}
) {
  const opts = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  }

  let number = +(value ?? 0)
  if (isNaN(number)) number = 0

  let string = number.toLocaleString(locale, opts)
  /**
   * Fix for '-0'
   * */
  if (number === 0 && string.startsWith('-')) string = string.slice(1)

  return string
}

export function calcDiscountedPrice(item: ProductListItem) {
  const price = item.price ?? 0
  const discount = getActiveProductDiscount(item.discounts)
  const amount = discount?.amount ?? 0

  if (!amount) return price

  if (discount?.absolute) return price - amount

  return price - price * (amount / 100)
}

export function checkIfChecked(discounts: Discount[], id: Maybe<ID>) {
  const discount = getActiveProductDiscount(discounts)

  return (discount?.id ?? null) === id
}
/**
 * Вместо поля discount.name
 * */
export function getDiscountName(discounts: Discount[]) {
  const discount = getActiveProductDiscount(discounts)

  return getDiscountCaption(discount)
}

export function getDiscountCaption(discount?: Maybe<Discount | any>) {
  if (!discount?.amount) return 'нет'

  if (discount.absolute) return String(discount.amount)

  return `${discount.amount}%`
}

export function getActiveProductDiscount(
  discounts: Discount[]
): Maybe<Discount> {
  const activeDiscounts = discounts?.filter?.((discount) => discount.active)

  return findByPropValue(activeDiscounts, 'product', 'type')
}
